import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";

const figmaFontStyles = {
  merriweather700: {
    fontFamily: "Merriweather, serif",
    fontWeight: 700,
  },
  poppins400: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
  },
  poppins500: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
  },
  poppins700: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
  },
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpTimer, setOtpTimer] = useState(300); // 5 minutes in seconds
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useUser();

  useEffect(() => {
    let interval;
    if (otpSent && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setOtpSent(false);
      setErrorMessage("OTP expired. Please request a new one.");
    }
    return () => clearInterval(interval);
  }, [otpSent, otpTimer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleSendOtp = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      // Check if email exists in database
      const userResponse = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }/users/email?email=${encodeURIComponent(email)}`
      );
      const userData = await userResponse.json();
      if (!userData || !userData.id) {
        setErrorMessage(
          "Email not found. Please click 'Get Started' below to fill in your details."
        );
        setIsLoading(false);
        return;
      }
      // Send OTP
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        setOtpTimer(300);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error sending OTP. Please try again.");
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp }),
        }
      );
      const data = await response.json();
      if (data.success) {
        // Fetch user information
        const userResponse = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/users/email?email=${encodeURIComponent(email)}`
        );
        const userData = await userResponse.json();
        if (userData && userData.id) {
          setUser({
            id: userData.id,
            name: userData.name,
            email: userData.email,
          });
          navigate(`/dashboard/${userData.id}`);
        } else {
          setErrorMessage("User not found. Please try again.");
        }
      } else {
        setErrorMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error verifying OTP. Please try again.");
    }
    setIsLoading(false);
  };

  const handleNavigateToSignup = () => {
    navigate("/"); // LandingPage.js
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F1F5F8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: "12px",
          width: { xs: "95vw", sm: 400 },
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{ ...figmaFontStyles.merriweather700, mb: 1 }}
        >
          Welcome Back!
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ ...figmaFontStyles.poppins400, color: "#555", mb: 3 }}
        >
          Please enter your registered email to access your dashboard.
        </Typography>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
          sx={{ width: "100%" }}
        >
          <TextField
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            margin="normal"
            disabled={otpSent || isLoading}
            inputProps={{ "aria-label": "Email" }}
          />
          {otpSent && (
            <>
              <TextField
                label="OTP"
                variant="outlined"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                fullWidth
                margin="normal"
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  "aria-label": "OTP",
                }}
                disabled={isLoading}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 1, textAlign: "center" }}
              >
                OTP valid for: {formatTime(otpTimer)}
              </Typography>
            </>
          )}
          {errorMessage && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 1, mb: 1, textAlign: "center" }}
              role="alert"
            >
              {errorMessage}
            </Typography>
          )}
          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={otpSent ? handleVerifyOtp : handleSendOtp}
            disabled={isLoading || (otpSent && otpTimer === 0)}
            sx={{
              backgroundColor: "#DF7468",
              color: "#fff",
              mt: 2,
              mb: 1,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "16px",
              py: 1.5,
              "&:hover": { backgroundColor: "#C86254" },
              "&.Mui-disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
            aria-label={otpSent ? "Verify OTP" : "Send OTP"}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : otpSent ? (
              "Verify OTP"
            ) : (
              "Send OTP"
            )}
          </Button>
        </Box>
        <Box sx={{ mt: 5, width: "100%", textAlign: "center" }}>
          <Typography variant="body2" sx={{ ...figmaFontStyles.poppins400 }}>
            New user?
            <Button
              variant="contained"
              onClick={handleNavigateToSignup}
              sx={{
                ml: 1,
                textTransform: "none",
                backgroundColor: "#000000",
                color: "#ffffff",
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: "14px",
                px: 3,
                py: 1,
                "&:hover": { backgroundColor: "#333333" },
                "&.Mui-disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
              aria-label="Get Started"
            >
              Get Started
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
